import { createContext, FC, ReactNode, useContext, useRef, useState } from 'react'
import { importOs } from '../../Service/os'

export type BatchStatus = 'pending' | 'running' | 'success' | 'error'

export interface BatchItem {
  osId: number
  numero: number
  evento: string
  produtor: string
  total: number
  status: BatchStatus
  erro: string
}

export type BatchInput = Pick<BatchItem, 'osId' | 'numero' | 'evento' | 'produtor' | 'total'>

// Mesmos códigos de erro do gate de importação no backend, em PT amigável.
const ERRO_MSG: Record<string, string> = {
  FECHAMENTO_NAO_ENCONTRADO: 'O evento ainda não possui fechamento no P2.',
  FECHAMENTO_NAO_PRE_SUBMETIDO: 'Fechamento do evento não está em "pré-submetido".',
  OS_NAO_APROVADA: 'A OS precisa estar aprovada.',
  OS_JA_IMPORTADA: 'Esta OS já foi importada.',
  OS_SEM_ITENS_A_LANCAR: 'Não há valores a lançar nesta OS.',
}
const friendly = (e: any): string => {
  const code = e?.response?.data?.message
  return ERRO_MSG[code] ?? code ?? 'Erro na importação'
}

interface ImportBatchCtx {
  batch: BatchItem[]
  startBatch: (items: BatchInput[]) => void
  reprocessarOS: (osId: number) => void
  reprocessarErros: () => void
}

const Ctx = createContext<ImportBatchCtx | null>(null)

export const ImportBatchProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [batch, setBatch] = useState<BatchItem[]>([])
  const batchRef = useRef<BatchItem[]>([])
  const runningRef = useRef(false)

  const commit = (next: BatchItem[]) => {
    batchRef.current = next
    setBatch(next)
  }
  const patch = (osId: number, p: Partial<BatchItem>) =>
    commit(batchRef.current.map((b) => (b.osId === osId ? { ...b, ...p } : b)))

  // Fila sequencial: processa um por vez chamando o importOs real.
  const runQueue = async () => {
    if (runningRef.current) return
    runningRef.current = true
    try {
      let next = batchRef.current.find((b) => b.status === 'pending')
      while (next) {
        const osId = next.osId
        patch(osId, { status: 'running', erro: '' })
        try {
          await importOs(osId)
          patch(osId, { status: 'success', erro: '' })
        } catch (e) {
          patch(osId, { status: 'error', erro: friendly(e) })
        }
        next = batchRef.current.find((b) => b.status === 'pending')
      }
    } finally {
      runningRef.current = false
    }
  }

  const startBatch = (items: BatchInput[]) => {
    commit(items.map((i) => ({ ...i, status: 'pending' as BatchStatus, erro: '' })))
    setTimeout(runQueue, 50)
  }
  const reprocessarOS = (osId: number) => {
    patch(osId, { status: 'pending', erro: '' })
    setTimeout(runQueue, 50)
  }
  const reprocessarErros = () => {
    commit(batchRef.current.map((b) => (b.status === 'error' ? { ...b, status: 'pending', erro: '' } : b)))
    setTimeout(runQueue, 50)
  }

  return <Ctx.Provider value={{ batch, startBatch, reprocessarOS, reprocessarErros }}>{children}</Ctx.Provider>
}

export const useImportBatch = (): ImportBatchCtx => {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useImportBatch deve estar dentro de ImportBatchProvider')
  return ctx
}
